import React from "react";
import { StyleSheet, View } from "react-native";
import { Box, Text } from "native-base";
import { ScrollView } from "react-native-gesture-handler";

import { BackButtonBackHome } from "../../../../Universal/Buttons/BackButtonBackHomeModified";
import { useAppContext } from "../../../../Universal/API/Context-API/AppContext";
import { webFont } from "../../../../Universal/API/API";
import { useAPI } from "../../../../Universal/API/API";
import SimpleCenteredSpinner from "../../../../Universal/SimpleCenteredSpinner/SimpleCenteredSpinner";
import CatalogPdfCard from "./Components/CatalogPdfCard";

interface GeneralCatalogItem {
  id: string;
  title: string;
  file_name: string;
  pdf_url: string;
  cover_url?: string | null;
}

const createStyles = (scaleAll: number) =>
  StyleSheet.create({
    screen: {
      flex: 1,
      backgroundColor: "#FFFFFF",
    },
    scrollContent: {
      paddingTop: 42 * scaleAll,
      paddingBottom: 88 * scaleAll,
      paddingHorizontal: 16 * scaleAll,
    },
    shell: {
      width: "100%",
      maxWidth: 1560 * scaleAll,
      alignSelf: "center",
    },
    hero: {
      paddingTop: 10 * scaleAll,
      paddingHorizontal: 4 * scaleAll,
    },
    heroTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 22 * scaleAll,
      lineHeight: 27 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 4 * scaleAll,
      maxWidth: 900 * scaleAll,
    },
    gridWrap: {
      marginTop: 10 * scaleAll,
      flexDirection: "row",
      flexWrap: "wrap",
      gap: 10 * scaleAll,
      alignItems: "flex-start",
    },
    sectionWrap: {
      marginTop: 14 * scaleAll,
    },
    sectionTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 16 * scaleAll,
      lineHeight: 20 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 2 * scaleAll,
      paddingHorizontal: 4 * scaleAll,
    },
    emptyWrap: {
      marginTop: 10 * scaleAll,
      borderRadius: 22 * scaleAll,
      borderWidth: 1,
      borderColor: "#ECE8E1",
      backgroundColor: "#FBFBFA",
      paddingHorizontal: 18 * scaleAll,
      paddingVertical: 28 * scaleAll,
      alignItems: "center",
      justifyContent: "center",
    },
    emptyTitle: {
      fontFamily: webFont("Inter"),
      fontSize: 16 * scaleAll,
      lineHeight: 20 * scaleAll,
      color: "#111111",
      fontWeight: "700",
      marginBottom: 6 * scaleAll,
      textAlign: "center",
    },
    emptyText: {
      fontFamily: webFont("Inter"),
      fontSize: 13 * scaleAll,
      lineHeight: 18 * scaleAll,
      color: "#6D675F",
      fontWeight: "500",
      textAlign: "center",
    },
  });

const Catalogs: React.FC<any> = ({ navigation }) => {
  const {
    allCollectionsData,
    isSmallVersion,
    isMediumVersion,
    scaleAll,
  } = useAppContext();
  const { sendFormDataPostRequest } = useAPI();
  const styles = React.useMemo(() => createStyles(scaleAll), [scaleAll]);
  const [generalCatalogs, setGeneralCatalogs] = React.useState<GeneralCatalogItem[]>([]);
  const [generalCatalogsLoading, setGeneralCatalogsLoading] = React.useState(false);

  const catalogs = React.useMemo(() => {
    if (!allCollectionsData) return [];

    return Object.values(allCollectionsData)
      .filter((item) => !!item.link_pdf)
      .sort((a, b) => a.collection_name.localeCompare(b.collection_name, "ru"));
  }, [allCollectionsData]);

  const cardWidth = React.useMemo(() => {
    if (isSmallVersion) return "48.7%";
    if (isMediumVersion) return "49.1%";
    return "24.2%";
  }, [isMediumVersion, isSmallVersion]);

  React.useEffect(() => {
    let cancelled = false;

    const loadCatalogs = async () => {
      try {
        setGeneralCatalogsLoading(true);
        const response = await sendFormDataPostRequest({
          link: "list-catalogs",
          formData: new FormData(),
        });

        if (cancelled) return;

        if (response?.success && Array.isArray(response.catalogs)) {
          setGeneralCatalogs(response.catalogs);
          return;
        }

        setGeneralCatalogs([]);
      } catch (error) {
        if (!cancelled) {
          console.error("list-catalogs error", error);
          setGeneralCatalogs([]);
        }
      } finally {
        if (!cancelled) {
          setGeneralCatalogsLoading(false);
        }
      }
    };

    void loadCatalogs();

    return () => {
      cancelled = true;
    };
  }, [sendFormDataPostRequest]);

  return (
    <View style={styles.screen}>
      <View style={{ position: "absolute", width: "100%", left: 0, zIndex: 10000 }}>
        <BackButtonBackHome navigation={navigation} />
      </View>

      <ScrollView contentContainerStyle={styles.scrollContent}>
        <View style={styles.shell}>
          <View style={styles.hero}>
            <Text selectable={false} style={styles.heroTitle}>
              Каталоги Deante
            </Text>
          </View>

          {!allCollectionsData ? (
            <Box height={320 * scaleAll} justifyContent="center" alignItems="center">
              <SimpleCenteredSpinner scaleAll={scaleAll} width={22} height={22} />
            </Box>
          ) : (
            <>
              {catalogs.length > 0 ? (
                <View style={styles.sectionWrap}>
                  <Text selectable={false} style={styles.sectionTitle}>
                    Каталоги коллекций
                  </Text>
                  <View style={styles.gridWrap}>
                    {catalogs.map((collection) => (
                      <CatalogPdfCard
                        key={collection.collection_name}
                        title={collection.collection_name}
                        pdfUrl={collection.link_pdf}
                        coverImage={
                          collection.collection_small_image_slides_small_mmg?.[0] ||
                          collection.collection_full_image_gallery_slides?.[0] ||
                          collection.collection_small_image_gallery_slides_s?.[0] ||
                          null
                        }
                        chips={[
                          `${collection.collection_full_image_gallery_slides?.length ||
                            collection.collection_small_image_gallery_slides_s?.length ||
                            collection.collection_small_image_slides_small_mmg?.length ||
                            0} фото`,
                          "Коллекция",
                        ]}
                        scaleAll={scaleAll}
                        width={cardWidth}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {generalCatalogsLoading ? (
                <Box height={120 * scaleAll} justifyContent="center" alignItems="center">
                  <SimpleCenteredSpinner scaleAll={scaleAll} width={18} height={18} />
                </Box>
              ) : generalCatalogs.length > 0 ? (
                <View style={styles.sectionWrap}>
                  <Text selectable={false} style={styles.sectionTitle}>
                    Общие каталоги
                  </Text>
                  <View style={styles.gridWrap}>
                    {generalCatalogs.map((catalog) => (
                      <CatalogPdfCard
                        key={catalog.id}
                        title={catalog.title}
                        pdfUrl={catalog.pdf_url}
                        coverImage={catalog.cover_url ?? null}
                        chips={["PDF", "Каталог"]}
                        scaleAll={scaleAll}
                        width={cardWidth}
                      />
                    ))}
                  </View>
                </View>
              ) : null}

              {catalogs.length === 0 && generalCatalogs.length === 0 && !generalCatalogsLoading ? (
                <View style={styles.emptyWrap}>
                  <Text selectable={false} style={styles.emptyTitle}>
                    PDF-каталоги не найдены
                  </Text>
                  <Text selectable={false} style={styles.emptyText}>
                    Когда каталоги будут подключены, они появятся здесь.
                  </Text>
                </View>
              ) : null}
            </>
          )}
        </View>
      </ScrollView>
    </View>
  );
};

export default Catalogs;
